import { useRef, useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Eraser, CheckCircle } from '@phosphor-icons/react';

export default function SignaturePad({ onSaved }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const c = canvasRef.current;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.strokeStyle = '#1F51FF';
    ctx.lineWidth = 5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) * (canvasRef.current.width / rect.width),
      y: (e.clientY - rect.top) * (canvasRef.current.height / rect.height),
    };
  };

  const start = (e) => {
    e.preventDefault();
    canvasRef.current.setPointerCapture(e.pointerId);
    drawing.current = true;
    const ctx = canvasRef.current.getContext('2d');
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const p = pos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    setHasInk(true);
  };

  const end = () => { drawing.current = false; };

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, c.width, c.height);
    setHasInk(false);
  };

  const save = () => {
    if (!hasInk) { toast.error('Veuillez signer avant d\'enregistrer'); return; }
    setSaving(true);
    canvasRef.current.toBlob(async (blob) => {
      try {
        const formData = new FormData();
        formData.append('file', blob, 'signature.png');
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload/photo`, {
          method: 'POST', credentials: 'include', body: formData,
        });
        if (res.ok) {
          const result = await res.json();
          onSaved(`${process.env.REACT_APP_BACKEND_URL}/api/files/${result.file_id}`);
        } else { toast.error('Erreur lors de l\'enregistrement'); }
      } catch { toast.error('Erreur lors de l\'enregistrement'); }
      finally { setSaving(false); }
    }, 'image/png');
  };

  return (
    <div className="w-full space-y-3" data-testid="signature-pad">
      <canvas
        ref={canvasRef}
        width={500}
        height={300}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerLeave={end}
        className="w-full bg-white rounded-md border-2 border-dashed border-[#334155] cursor-crosshair touch-none"
        style={{ touchAction: 'none' }}
        data-testid="signature-canvas"
      />
      <p className="text-xs text-[#64748B] text-center">Signez ci-dessus avec votre doigt, stylet ou souris</p>
      <div className="flex gap-2">
        <Button type="button" variant="outline" onClick={clear} className="flex-1 border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B]" data-testid="clear-signature-button">
          <Eraser size={16} className="mr-2" /> Effacer
        </Button>
        <Button type="button" onClick={save} disabled={saving || !hasInk} className="flex-1 bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="save-signature-button">
          <CheckCircle size={16} className="mr-2" /> {saving ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </div>
    </div>
  );
}
